import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { formatUSD, formatCLP, formatDateTime } from "../../lib/format";

const STATUS_LABEL = {
    active: "Activa",
    pending: "Pendiente de pago",
    cancelled: "Cancelada",
    expired: "Expirada",
    past_due: "Cobro fallido",
};

export default function MySubscriptions() {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/me/subscriptions");
            setSubs(data || []);
        } catch (err) { toast.error(apiErrorMessage(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const cancel = async (s) => {
        if (!window.confirm("¿Cancelar esta suscripción? Dejará de renovarse, pero conservas el acceso hasta el final del período ya pagado.")) return;
        setCancelling(s.id);
        try {
            await api.post(`/me/subscriptions/${s.id}/cancel`);
            toast.success("Suscripción cancelada. Mantienes el acceso hasta el fin del período pagado.");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
        finally { setCancelling(""); }
    };

    return (
        <DashboardLayout role="client">
            <div className="mb-8" data-testid="my-subscriptions-page">
                <div className="label-mono mb-3">/ Suscripciones</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Mis suscripciones</h1>
                <p className="text-sm text-avx-muted mt-2 max-w-2xl">
                    Tus bundles por suscripción. Mientras la suscripción esté activa, las licencias de los plugins se renuevan automáticamente.
                </p>
            </div>

            {loading ? (
                <div className="panel p-10 text-center text-avx-muted">Cargando…</div>
            ) : subs.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted" data-testid="no-subscriptions">
                    No tienes suscripciones activas. <Link to="/plugins" className="text-avx-blue hover:underline">Explora los bundles →</Link>
                </div>
            ) : (
                <div className="space-y-4">
                    {subs.map((s) => (
                        <div key={s.id} className="panel-elevated p-5" data-testid={`subscription-${s.id}`}>
                            <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
                                <div>
                                    <div className="flex items-center gap-3 flex-wrap mb-2">
                                        <span className="font-bold text-base">{s.bundle?.name || "Bundle"}</span>
                                        <StatusBadge status={s.status} testId={`sub-status-${s.id}`} />
                                        <span className="label-mono">{s.period_label} · {formatUSD(s.amount)}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {(s.bundle?.products || []).map((p) => (
                                            <span key={p.id} className="px-2 py-0.5 rounded-sm bg-avx-bg/60 border border-avx-line text-[10px] font-mono">{p.name}</span>
                                        ))}
                                    </div>
                                    {s.current_period_end && (
                                        <div className="text-xs text-avx-muted">
                                            {s.cancel_at_period_end
                                                ? <>Acceso hasta <span className="text-avx-text">{formatDateTime(s.current_period_end)}</span> (no se renovará)</>
                                                : <>Próxima renovación: <span className="text-avx-text">{formatDateTime(s.current_period_end)}</span></>}
                                        </div>
                                    )}
                                    {s.status === "pending" && (
                                        <div className="text-xs text-avx-blue mt-1">Esperando confirmación de PayPal…</div>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Link to="/dashboard/plugins" className="text-xs font-mono uppercase tracking-[0.18em] border border-avx-line px-3 py-1 rounded-sm text-avx-blue hover:bg-avx-blue/10">Mis plugins</Link>
                                    {(s.status === "active" || s.status === "past_due") && !s.cancel_at_period_end && (
                                        <button
                                            onClick={() => cancel(s)}
                                            disabled={cancelling === s.id}
                                            data-testid={`cancel-sub-${s.id}`}
                                            className="text-xs font-mono uppercase tracking-[0.18em] border border-avx-danger/30 px-3 py-1 rounded-sm text-avx-danger hover:bg-avx-danger/10 disabled:opacity-60"
                                        >
                                            {cancelling === s.id ? "Cancelando…" : "Cancelar"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </DashboardLayout>
    );
}
